import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Chip,
  Button,
  IconButton,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Undo as UndoIcon,
  Person as PersonIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
} from '@mui/icons-material';
import { collection, query, where, onSnapshot, updateDoc, doc, Timestamp, orderBy as firestoreOrderBy } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Order, OrderItem } from '../types';
import { getLocalizedProductName } from '../utils/localization';

// 商品を個別アイテムに展開する型
interface ExpandedOrderItem extends OrderItem {
  orderId: string;
  orderNumber: string;
  tableNumber: string;
  orderedAt: Date;
  itemId: string;
  itemIndex: number;
  totalQuantity: number;
  // 注文者情報
  orderedByStaffName?: string;
  isStaffOrder?: boolean;
}

// 通知音を生成する関数（Web Audio API）
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // 最初のビープ音
    const playBeep = (startTime: number, frequency: number, duration: number) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    const now = audioContext.currentTime;
    // ピンポン音（2回）
    playBeep(now, 880, 0.15);        // 高い音
    playBeep(now + 0.2, 660, 0.15);  // 低い音
    playBeep(now + 0.5, 880, 0.15);  // 高い音
    playBeep(now + 0.7, 660, 0.2);   // 低い音（長め）
  } catch (e) {
    console.warn('通知音の再生に失敗:', e);
  }
};

const Orders: React.FC = () => {
  const { shop } = useAuth();
  const { language } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [orders, setOrders] = useState<Order[]>([]);
  const [expandedItems, setExpandedItems] = useState<ExpandedOrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [cancelDialog, setCancelDialog] = useState<{
    open: boolean;
    orderId: string;
    itemId: string;
    productId: string;
    productName: string;
  }>({ open: false, orderId: '', itemId: '', productId: '', productName: '' });
  const [markAsSoldOut, setMarkAsSoldOut] = useState(false);

  // 前回の注文IDを追跡して新規注文を検知
  const prevOrderIdsRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef(true);

  // 注文アイテムを個別商品に展開する関数
  const expandOrderItems = useCallback((orders: Order[]): ExpandedOrderItem[] => {
    const expanded: ExpandedOrderItem[] = [];

    orders.forEach(order => {
      order.items.forEach((item, itemIdx) => {
        if (item.itemId && item.itemIndex !== undefined) {
          expanded.push({
            ...item,
            orderId: order.id,
            orderNumber: order.orderNumber || '',
            tableNumber: order.tableNumber || '',
            orderedAt: order.orderedAt || order.createdAt,
            itemId: item.itemId,
            itemIndex: item.itemIndex,
            totalQuantity: item.quantity,
            orderedByStaffName: order.orderedBy?.staffName,
            isStaffOrder: order.orderedBy?.isStaffOrder,
          });
        } else {
          for (let i = 1; i <= item.quantity; i++) {
            const itemId = `${order.id}_item${itemIdx}_${i}`;
            expanded.push({
              ...item,
              orderId: order.id,
              orderNumber: order.orderNumber || '',
              tableNumber: order.tableNumber || '',
              orderedAt: order.orderedAt || order.createdAt,
              itemId: itemId,
              itemIndex: i,
              totalQuantity: item.quantity,
              itemStatus: item.itemStatus || 'pending',
              orderedByStaffName: order.orderedBy?.staffName,
              isStaffOrder: order.orderedBy?.isStaffOrder,
            });
          }
        }
      });
    });

    return expanded;
  }, []);

  useEffect(() => {
    if (!shop) return;

    // 会計済み（completed）以外の注文のみ取得。
    // served を除いていたため、「提供済み」タブが常に 0 件だった
    // （DB に served が10件あるのに 0 と表示）。
    // 会計画面 (Checkout.tsx) は served を「開いた伝票」として扱うので、
    // 会計には出るのに注文管理には出ない、という片側落ちになっていた。
    const q = query(
      collection(db, 'orders'),
      where('shopId', '==', shop.id),
      where('status', 'in', ['pending', 'confirmed', 'preparing', 'ready', 'served']),
      firestoreOrderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ordersData = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          orderedAt: d.data().orderedAt?.toDate(),
          completedAt: d.data().completedAt?.toDate(),
          createdAt: d.data().createdAt?.toDate(),
          updatedAt: d.data().updatedAt?.toDate(),
        })) as Order[];
        setOrders(ordersData);

        // 新規注文の検知と通知音
        const currentOrderIds = new Set(ordersData.map(o => o.id));
        if (!isInitialLoadRef.current && soundEnabled) {
          const newOrders = ordersData.filter(o => !prevOrderIdsRef.current.has(o.id));
          if (newOrders.length > 0) {
            playNotificationSound();
          }
        }
        prevOrderIdsRef.current = currentOrderIds;
        isInitialLoadRef.current = false;

        const expanded = expandOrderItems(ordersData);
        setExpandedItems(expanded);

        setLoading(false);
      },
      (err) => {
        console.error('注文取得エラー:', err);
        setError('注文の取得に失敗しました');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [shop, soundEnabled, expandOrderItems]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // ステータスラベル
  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: '注文受付',
      preparing: '調理中',
      ready: '調理完了',
      served: '提供済み',
      cancelled: 'キャンセル',
    };
    return labels[status] || '注文受付';
  };

  // ステータスカラー
  const getStatusColor = (status: string) => {
    const colors: Record<string, 'warning' | 'info' | 'success' | 'default' | 'error'> = {
      pending: 'warning',
      preparing: 'info',
      ready: 'success',
      served: 'default',
      cancelled: 'error',
    };
    return colors[status] || 'warning';
  };

  // 個別商品のステータス更新
  const handleUpdateItemStatus = async (
    orderId: string,
    itemId: string,
    newStatus: 'pending' | 'served' | 'cancelled'
  ) => {
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) return;

      const updatedItems = order.items.map(item => {
        const expanded = expandedItems.filter(ei =>
          ei.orderId === orderId &&
          ei.productId === item.productId
        );

        const matchingExpanded = expanded.find(ei => ei.itemId === itemId);
        if (matchingExpanded) {
          return {
            ...item,
            itemId: matchingExpanded.itemId,
            itemIndex: matchingExpanded.itemIndex,
            itemStatus: newStatus,
            servedAt: newStatus === 'served' ? Timestamp.now() : null,
          };
        }
        return item;
      });

      // キャンセル時は注文合計を再計算
      const activeItems = updatedItems.filter(item =>
        (item.itemStatus || 'pending') !== 'cancelled'
      );
      const newTotal = activeItems.reduce((sum, item) => {
        const optionsTotal = item.selectedOptions?.reduce((s: number, opt: any) => s + (opt.price || 0), 0) || 0;
        return sum + ((item.unitPrice || item.price) + optionsTotal) * (item.quantity || 1);
      }, 0);

      await updateDoc(doc(db, 'orders', orderId), {
        items: updatedItems,
        total: newTotal,
        subtotal: newTotal,
        updatedAt: Timestamp.now(),
      });

      const statusLabels: Record<string, string> = {
        pending: '注文受付',
        served: '提供済み',
        cancelled: 'キャンセル',
      };
      setSuccess(`ステータスを「${statusLabels[newStatus]}」に更新しました`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('ステータス更新エラー:', err);
      setError('ステータスの更新に失敗しました');
    }
  };

  const handleMarkAsServed = async (orderId: string, itemId: string) => {
    await handleUpdateItemStatus(orderId, itemId, 'served');
  };

  const handleRevertToPending = async (orderId: string, itemId: string) => {
    await handleUpdateItemStatus(orderId, itemId, 'pending');
  };

  // キャンセルダイアログを開く
  const handleCancelItem = (orderId: string, itemId: string) => {
    const item = expandedItems.find(ei => ei.itemId === itemId && ei.orderId === orderId);
    setCancelDialog({
      open: true,
      orderId,
      itemId,
      productId: item?.productId || '',
      productName: getLocalizedProductName(item || {} as any, language) || '不明',
    });
    setMarkAsSoldOut(false);
  };

  // キャンセル確定（売切設定含む）
  const handleConfirmCancel = async () => {
    const { orderId, itemId, productId } = cancelDialog;
    setCancelDialog(prev => ({ ...prev, open: false }));

    await handleUpdateItemStatus(orderId, itemId, 'cancelled');

    // 売切にするチェックがONの場合
    if (markAsSoldOut && productId) {
      try {
        await updateDoc(doc(db, 'products', productId), {
          displayStatus: 'soldout',
          updatedAt: Timestamp.now(),
        });
        setSuccess('商品をキャンセルし、売切に設定しました');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        console.error('売切設定エラー:', err);
        setError('商品の売切設定に失敗しました');
      }
    }
  };

  // アクティブな注文
  const activeItems = expandedItems.filter(item => {
    const order = orders.find(o => o.id === item.orderId);
    if (!order) return false;
    const status = item.itemStatus || 'pending';
    return status === 'pending' || status === 'preparing';
  });

  // 提供済み
  const servedItems = expandedItems.filter(item => {
    return item.itemStatus === 'served';
  });

  const filteredItems = (() => {
    switch (currentTab) {
      case 0:
        return activeItems;
      case 1:
        return servedItems;
      default:
        return activeItems;
    }
  })();

  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat('ja-JP', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const calculateItemTotal = (item: OrderItem) => {
    const optionsTotal = item.selectedOptions?.reduce((sum, opt) => sum + opt.price, 0) || 0;
    return (item.unitPrice || item.price) + optionsTotal;
  };

  // スマホ用カードレンダリング
  const renderMobileCard = (item: ExpandedOrderItem) => (
    <Card
      key={item.itemId}
      sx={{
        mb: 1.5,
        bgcolor: item.itemStatus === 'cancelled' ? 'grey.100' : 'inherit',
        opacity: item.itemStatus === 'cancelled' ? 0.6 : 1,
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* 1行目: テーブル + 時間 + ステータス */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`T${item.tableNumber}`}
              size="small"
              color="primary"
              sx={{ fontWeight: 'bold', minWidth: 40 }}
            />
            <Typography variant="caption" color="text.secondary">
              {formatTime(item.orderedAt)}
            </Typography>
            {/* 代理注文スタッフ名 */}
            {item.isStaffOrder && item.orderedByStaffName && (
              <Chip
                icon={<PersonIcon sx={{ fontSize: 14 }} />}
                label={item.orderedByStaffName}
                size="small"
                sx={{
                  bgcolor: 'purple.50',
                  color: 'purple.main',
                  fontSize: '0.7rem',
                  height: 22,
                  '& .MuiChip-icon': { color: 'purple.main' },
                }}
              />
            )}
          </Box>
          <Chip
            label={getStatusLabel(item.itemStatus || 'pending')}
            color={getStatusColor(item.itemStatus || 'pending')}
            size="small"
          />
        </Box>

        {/* 2行目: 商品名 + 金額 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight="bold">
              {getLocalizedProductName(item, language)}
            </Typography>
            {item.totalQuantity > 1 && (
              <Chip
                label={`${item.itemIndex}/${item.totalQuantity}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
          <Typography variant="body1" fontWeight="bold" sx={{ ml: 1 }}>
            ¥{calculateItemTotal(item).toLocaleString()}
          </Typography>
        </Box>

        {/* オプション */}
        {item.selectedOptions && item.selectedOptions.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            {item.selectedOptions.map((opt, idx) => (
              <Typography key={idx} variant="caption" color="text.secondary" display="block">
                {opt.choiceName} (+¥{opt.price})
              </Typography>
            ))}
          </Box>
        )}

        {/* 備考 */}
        {(item.notes || item.note) && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.5 }}>
            {item.notes || item.note}
          </Typography>
        )}

        {/* キャンセル情報 (商品単位) */}
        {item.itemStatus === 'cancelled' && (item as any).cancelInfo && (
          <Box sx={{ mt: 0.5, p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
            <Typography variant="caption" color="error.main" display="block">
              理由: {(item as any).cancelInfo.reason || '理由未入力'}
            </Typography>
            {(item as any).cancelInfo.staffName && (
              <Typography variant="caption" color="text.secondary" display="block">
                担当: {(item as any).cancelInfo.staffName}
              </Typography>
            )}
          </Box>
        )}

        {/* アクションボタン */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          {currentTab === 0 && item.itemStatus !== 'cancelled' && (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                fullWidth
                startIcon={<CheckCircleIcon />}
                onClick={() => handleMarkAsServed(item.orderId, item.itemId)}
                sx={{ py: 0.8 }}
              >
                調理完了
              </Button>
              <IconButton
                size="small"
                onClick={() => handleCancelItem(item.orderId, item.itemId)}
                color="error"
                title="キャンセル"
              >
                <CancelIcon />
              </IconButton>
            </>
          )}
          {currentTab === 1 && item.itemStatus === 'served' && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              fullWidth
              startIcon={<UndoIcon />}
              onClick={() => handleRevertToPending(item.orderId, item.itemId)}
            >
              注文受付に戻す
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // PC用テーブルレンダリング
  const renderDesktopTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>テーブル</TableCell>
            <TableCell>時間</TableCell>
            <TableCell>注文者</TableCell>
            <TableCell>商品名</TableCell>
            <TableCell>数量</TableCell>
            <TableCell>オプション</TableCell>
            <TableCell>備考</TableCell>
            <TableCell>金額</TableCell>
            <TableCell>ステータス</TableCell>
            <TableCell>操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredItems.map((item) => (
            <TableRow
              key={item.itemId}
              sx={{
                bgcolor: item.itemStatus === 'cancelled' ? 'grey.100' : 'inherit',
                opacity: item.itemStatus === 'cancelled' ? 0.6 : 1,
              }}
            >
              <TableCell>
                <Typography variant="body1" fontWeight="bold">
                  {item.tableNumber}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDateTime(item.orderedAt)}
                </Typography>
              </TableCell>
              <TableCell>
                {item.isStaffOrder && item.orderedByStaffName ? (
                  <Chip
                    icon={<PersonIcon sx={{ fontSize: 14 }} />}
                    label={item.orderedByStaffName}
                    size="small"
                    sx={{
                      bgcolor: '#f3e5f5',
                      color: '#7b1fa2',
                      '& .MuiChip-icon': { color: '#7b1fa2' },
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    顧客
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {getLocalizedProductName(item, language)}
                </Typography>
                {item.totalQuantity > 1 && (
                  <Chip
                    label={`${item.itemIndex}/${item.totalQuantity}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </TableCell>
              <TableCell>1</TableCell>
              <TableCell>
                {item.selectedOptions && item.selectedOptions.length > 0 ? (
                  <Box>
                    {item.selectedOptions.map((opt, idx) => (
                      <Typography key={idx} variant="caption" display="block">
                        {opt.choiceName} (+¥{opt.price})
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">-</Typography>
                )}
              </TableCell>
              <TableCell>
                {item.notes || item.note ? (
                  <Typography variant="caption">{item.notes || item.note}</Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">-</Typography>
                )}
                {item.itemStatus === 'cancelled' && (item as any).cancelInfo && (
                  <Box sx={{ mt: 0.5, p: 0.5, bgcolor: 'error.50', borderRadius: 0.5 }}>
                    <Typography variant="caption" color="error.main" display="block">
                      理由: {(item as any).cancelInfo.reason || '理由未入力'}
                    </Typography>
                    {(item as any).cancelInfo.staffName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        担当: {(item as any).cancelInfo.staffName}
                      </Typography>
                    )}
                  </Box>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  ¥{calculateItemTotal(item).toLocaleString()}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={getStatusLabel(item.itemStatus || 'pending')}
                  color={getStatusColor(item.itemStatus || 'pending')}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {currentTab === 0 && item.itemStatus !== 'cancelled' && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleMarkAsServed(item.orderId, item.itemId)}
                      >
                        調理完了
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => handleCancelItem(item.orderId, item.itemId)}
                        color="error"
                        title="キャンセル"
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  {currentTab === 1 && item.itemStatus === 'served' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<UndoIcon />}
                      onClick={() => handleRevertToPending(item.orderId, item.itemId)}
                    >
                      注文受付に戻す
                    </Button>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ px: isMobile ? 1 : 3 }}>
      <Box sx={{ mb: isMobile ? 1.5 : 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'}>注文管理</Typography>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              注文を確認し、調理完了で提供済みにします
            </Typography>
          )}
        </Box>
        {/* 通知音ON/OFFボタン */}
        <IconButton
          onClick={() => setSoundEnabled(!soundEnabled)}
          color={soundEnabled ? 'primary' : 'default'}
          title={soundEnabled ? '通知音ON' : '通知音OFF'}
          sx={{ mt: 0.5 }}
        >
          {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: isMobile ? 1.5 : 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant={isMobile ? 'fullWidth' : 'standard'}>
          <Tab label={`注文受付 (${activeItems.length})`} />
          <Tab label={`提供済み (${servedItems.length})`} />
        </Tabs>
      </Paper>

      {filteredItems.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {currentTab === 0 ? '注文受付中の商品はありません' : '提供済みの商品はありません'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        // スマホ: カードレイアウト
        <Box>
          {filteredItems.map(item => renderMobileCard(item))}
        </Box>
      ) : (
        // PC: テーブルレイアウト
        renderDesktopTable()
      )}
      {/* キャンセル確認ダイアログ */}
      <Dialog
        open={cancelDialog.open}
        onClose={() => setCancelDialog(prev => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>商品キャンセル</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            「{cancelDialog.productName}」をキャンセルしますか？
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={markAsSoldOut}
                onChange={(e) => setMarkAsSoldOut(e.target.checked)}
                color="error"
              />
            }
            label="この商品を売切にする"
          />
          {markAsSoldOut && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
              他の顧客のメニューでも「売切」と表示されます
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(prev => ({ ...prev, open: false }))}>
            戻る
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
          >
            キャンセル実行
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Orders;
